import {
  Box,
  Tag as ChakraTag,
  Flex,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import * as BoundaryService from "src/components/Boundary/services";
import * as TaggingInfoDialog from "src/components/Dialogs/TaggingInfoDialog";
import { BoundaryTag } from "src/components/Tags/BoundaryTag";
import { FormSchoolRankSubStatusIndex } from "src/constants";
import { useFormTemplateSettings } from "src/hooks/useFormTemplateSettings";
import { useTimezoneName } from "src/hooks/useOrganization";
import { WEGLOT_SKIP_CLASS } from "src/plugins/weglot/constants";
import { isOfferExpired } from "src/services/form/offer";
import * as GQL from "src/types/graphql";
import { shouldShowApplicationSchoolRankOptionsMenu } from "../helpers";
import * as OptionsMenu from "./OptionsMenu";
import * as SubStatusTag from "./SubStatusTag";

export type Form = {
  readonly id: uuid;
  readonly status: GQL.form_status_enum;
  readonly form_template: {
    readonly key: string;
  };
};
export type Offer = OptionsMenu.Offer;
export type Waitlist = SubStatusTag.Waitlist;
export type RankedSchool = {
  readonly sub_status: string | null;
  readonly tags: readonly Tag[];
};
export type Tag = {
  readonly id: uuid;
  readonly enrollment_period_tag: {
    readonly name: string;
    readonly description: string | null;
  };
};
export type School = BoundaryService.WithBoundaryTags<OptionsMenu.School>;
export interface SingleSchoolRankProps {
  formTemplateId: uuid;
  acceptedOfferSchoolName: string | undefined;
  form: Form;
  offer: Offer | null;
  formSchoolRank: RankedSchool;
  waitlist: Waitlist | null;
  gradeLabel: string;
  school: School;
  studentName: string;
  enrollmentPeriodKey: string | null;
  showWaitlistPosition: boolean;
}
export function SingleSchoolRank(props: SingleSchoolRankProps) {
  const {
    formTemplateId,
    acceptedOfferSchoolName,
    form,
    offer,
    formSchoolRank,
    waitlist,
    gradeLabel,
    school,
    studentName,
    enrollmentPeriodKey,
    showWaitlistPosition,
  } = props;

  const timezoneName = useTimezoneName();

  const [isActionPending, setIsActionPending] = useState(false);

  const { onOpen, onClose, isOpen } = useDisclosure();

  const showSubstatus =
    (form.status === GQL.form_status_enum.Submitted ||
      form.status === GQL.form_status_enum.Verified ||
      form.status === GQL.form_status_enum.LotteryReady ||
      form.status === GQL.form_status_enum.Admissions) &&
    !!formSchoolRank.sub_status;

  const formTemplateSettings = useFormTemplateSettings(formTemplateId);

  const showOptionsMenu =
    formTemplateSettings.hasData() &&
    shouldShowApplicationSchoolRankOptionsMenu(
      offer,
      waitlist,
      formTemplateSettings.data.allowDeclineAcceptedOffer
    ) &&
    (!offer ||
      !isOfferExpired({
        expires_at: offer.expires_at,
        timezone_name: timezoneName,
      }));

  const { tags, firstTag } = useMemo(() => {
    const boundaryTags: readonly TaggingInfoDialog.Tag[] = (
      school.boundaryTags ?? []
    ).map((name) => ({
      type: "BoundaryTag",
      name,
    }));

    const formSchoolRankTags: readonly TaggingInfoDialog.Tag[] = _.orderBy(
      formSchoolRank.tags,
      (tag) => tag.enrollment_period_tag.name.toLocaleLowerCase()
    ).map((tag) => ({
      type: "FormSchoolRankTag",
      id: tag.id,
      name: tag.enrollment_period_tag.name,
      description: tag.enrollment_period_tag.description,
    }));

    const tags: readonly TaggingInfoDialog.Tag[] =
      boundaryTags.concat(formSchoolRankTags);

    const tag = tags[0];
    let firstTag: React.ReactNode = null;
    switch (tag?.type) {
      case "FormSchoolRankTag":
        firstTag = (
          <ChakraTag key={tag.id} variant="tag" fontSize="xs">
            <Box whiteSpace="nowrap">{tag.name}</Box>
          </ChakraTag>
        );
        break;
      case "BoundaryTag":
        firstTag = <BoundaryTag tag={tag} fontSize="xs" />;
        break;
    }

    return {
      tags,
      firstTag,
    };
  }, [formSchoolRank.tags, school.boundaryTags]);

  const allTagsCount = tags.length;
  const formSchoolRankTagging = useMemo(() => {
    if (allTagsCount > 0) {
      return (
        <Flex gap={1}>
          {showSubstatus && firstTag}
          {allTagsCount > 1 && (
            <ChakraTag
              key="form-school-tag"
              variant="tag"
              fontSize="xs"
              cursor="pointer"
            >
              <Box>+{allTagsCount - 1} more</Box>
            </ChakraTag>
          )}
        </Flex>
      );
    }
  }, [allTagsCount, showSubstatus, firstTag]);

  return (
    <Flex alignItems="center" gap={2} flexGrow={1}>
      <Flex
        direction="column"
        cursor={isActionPending ? "default" : "pointer"}
        onClick={isActionPending ? undefined : onOpen}
        minW={0}
      >
        <Flex alignItems="start" gap="2" flexWrap="wrap">
          <Box className={WEGLOT_SKIP_CLASS} flexShrink={0} maxW="100%">
            <Text>{school.name}</Text>
          </Box>
          <Box flexShrink={0}>
            {showSubstatus ? (
              <SubStatusTag.SubStatusTag
                formSchoolRankSubStatus={
                  formSchoolRank.sub_status as FormSchoolRankSubStatusIndex
                }
                waitlist={waitlist}
                offer={offer}
                showWaitlistPosition={showWaitlistPosition}
                timezoneName={timezoneName}
                isActionPending={isActionPending}
              />
            ) : (
              firstTag
            )}
          </Box>
        </Flex>
        {formSchoolRankTagging}
      </Flex>

      {showOptionsMenu && (
        <OptionsMenu.OptionsMenu
          formTemplateId={formTemplateId}
          acceptedOfferSchoolName={acceptedOfferSchoolName}
          formId={form.id}
          formKey={form.form_template.key}
          offer={offer}
          waitlist={waitlist}
          gradeLabel={gradeLabel}
          school={school}
          studentName={studentName}
          enrollmentPeriodKey={enrollmentPeriodKey}
          isActionPending={isActionPending}
          onActionPendingChange={setIsActionPending}
        />
      )}

      <TaggingInfoDialog.TaggingInfoDialog
        isOpen={isOpen}
        offer={offer}
        waitlist={waitlist}
        onCancel={onClose}
        schoolName={school.name}
        tags={tags}
        subStatus={formSchoolRank.sub_status as FormSchoolRankSubStatusIndex}
        showWaitlistPosition={showWaitlistPosition}
      />
    </Flex>
  );
}
